import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useHubAuth } from '@/context/HubAuthContext'
import ProjectEditor, { projectRowToForm } from '@/components/hub/ProjectEditor'
import { supabase } from '@/lib/supabase'

export default function HubEditorPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user } = useHubAuth()
  const isNew = !slug

  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(!isNew)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!user?.id || isNew) {
      setLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      setLoading(true)
      setLoadError('')
      const { data, error } = await supabase
        .from('hub_projects')
        .select('*')
        .eq('slug', slug)
        .eq('user_id', user.id)
        .maybeSingle()

      if (cancelled) return
      if (error) {
        setLoadError(error.message)
        setRow(null)
      } else if (!data) {
        setLoadError('Project not found.')
        setRow(null)
      } else {
        setRow(data)
      }
      setLoading(false)
    })()

    return () => {
      cancelled = true
    }
  }, [user?.id, slug, isNew])

  const handleSaved = ({ slug: savedSlug }) => {
    if (isNew && savedSlug) {
      navigate(`/hub/projects/${encodeURIComponent(savedSlug)}/edit`, { replace: true })
    }
  }

  if (!isNew && loading) {
    return <p className="text-sm text-muted-foreground">Loading project…</p>
  }

  if (!isNew && loadError) {
    return <p className="text-sm text-red-400">{loadError}</p>
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-lg font-semibold tracking-tight">{isNew ? 'New project' : 'Edit project'}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Data is stored in Supabase (<code className="font-mono text-xs">hub_projects</code>). Run{' '}
          <code className="font-mono text-xs">supabase/hub-projects-editor-foundation.sql</code> if the table is missing.
        </p>
      </div>
      {user?.id && (
        <ProjectEditor
          key={row?.id || 'new'}
          isNew={isNew}
          userId={user.id}
          existingId={row?.id || null}
          initialForm={row ? projectRowToForm(row) : null}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
